import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { authManager } from '../utils/auth';
import { isNonMmlAdmin } from '../utils/roles';
import {
    isValidHistoryItem, historyItemLabel, historyItemPoints,
} from '../utils/loyalty';
import MemberUnlockStatus from '../Components/MemberUnlockStatus';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Base_Url = process.env.REACT_APP_API_BASE_URL;

const formatDate = (d) =>
    d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }) : '-';

const formatDateTime = (d) =>
    d ? new Date(d).toLocaleString('en-US', { month: 'short', day: '2-digit', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '-';

// Loyalty balance is stored (and returned by /users/:id) as whole cents,
// already rounded down by squad-rewards — read as a cash figure, matching the
// Users list (Screens/User.js), never raw points.
const dollars = (cents) => `$${((Number.isFinite(cents) ? cents : 0) / 100).toFixed(2)}`;

// Points-history rows per page (client-side — the API returns the whole list).
const HISTORY_PAGE_SIZE = 10;

const Field = ({ label, value }) => (
    <div className="form-group row align-items-center">
        <label className="col-12 col-md-4 col-form-label fwt_600">{label}</label>
        <div className="col-12 col-md-8">{value ?? '-'}</div>
    </div>
);

const Users_View = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [userRole, setUserRole] = useState(null);
    // Loyalty bank statement (points history + totals) from squad-rewards via
    // the admin proxy. Best-effort and self-contained: 'loading' until the
    // call settles, then either `data` or `error` — a failure just collapses
    // the sub-section to a short note, it never blocks the rest of the page.
    const [loyalty, setLoyalty] = useState({ status: 'loading', data: null });
    // squad-rewards returns the member's full all-time history in one response
    // (no server-side paging) — page it client-side so a long-tenured member's
    // list stays scannable, same approach as the member-facing Loyalty Center.
    const [historyPage, setHistoryPage] = useState(1);
    // The Member page link (#195) is CircleOne-only, like the page itself.
    const [canViewReceipts, setCanViewReceipts] = useState(false);

    useEffect(() => {
        let cancelled = false;
        authManager.validateUser()
            .then((currentUser) => {
                if (!cancelled) setCanViewReceipts(isNonMmlAdmin(currentUser));
            })
            .catch(() => {});
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        let cancelled = false;
        setLoyalty({ status: 'loading', data: null });
        setHistoryPage(1);
        (async () => {
            try {
                const res = await authManager.authenticatedFetch(`${Base_Url}/admin/loyalty/members/${id}/balance`);
                const body = await res.json();
                if (cancelled) return;
                if (res.ok && body.status === 'success' && body.data) {
                    setLoyalty({ status: 'ready', data: body.data });
                } else {
                    setLoyalty({ status: 'error', data: null });
                }
            } catch (err) {
                if (process.env.NODE_ENV === 'development') console.error('Failed to load loyalty balance:', err);
                if (!cancelled) setLoyalty({ status: 'error', data: null });
            }
        })();
        return () => { cancelled = true; };
    }, [id]);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        const fetchUser = async () => {
            try {
                const validatedRole = await authManager.validateUserRole();
                setUserRole(validatedRole);
                const res = await authManager.authenticatedFetch(`${Base_Url}/users/${id}`);
                if (!res.ok) throw new Error('Fetch failed');
                const data = await res.json();
                setUser(data.result);
            } catch (err) {
                if (process.env.NODE_ENV === 'development') console.error('Failed to load user:', err);
                navigate('/login');
            } finally {
                setLoading(false);
            }
        };
        fetchUser();
    }, [id, navigate]);

    if (!loading && !user) return <div className="text-center mt-5">User not found.</div>;

    const handleReset2FA = async () => {
        if (!user?.email || !user?.role) { toast.error('User email or role is missing.'); return; }
        try {
            const response = await authManager.authenticatedFetch(`${Base_Url}/users/invite`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: user.email, role: user.role, source: user.source, forceReset: true }),
            });
            if (response.status === 200) {
                toast.success('Reset invite sent successfully!');
            } else {
                const data = await response.json();
                toast.error(data?.message || 'Failed to reset 2FA.');
            }
        } catch (err) {
            toast.error('Error sending 2FA reset invite.');
        }
    };

    const planLabel = () => {
        const s = user?.subscriptionStatus || user?.subscription_status;
        const planType = user?.currentPlan?.planType;
        const isPaidPlan = planType && planType !== 'free';
        if (s === 'active' && isPaidPlan) return <span className="f_green fwt_600">Paid</span>;
        if (s === 'trialing' && isPaidPlan) return <span className="text-warning fwt_600">Trial</span>;
        return <span className="text-muted">Free</span>;
    };

    return (
        <div>
            <ToastContainer autoClose={3000} />
            <div className="content-header">
                <div className="container-fluid">
                    <div className="row mb-2 align-items-center">
                        <div className="col-6">
                            <h1 className="m-0 txt_lft">View User</h1>
                        </div>
                        <div className="col-6">
                            <ol className="breadcrumb justify-content-end">
                                <li className="breadcrumb-item">
                                    <Link to="/users"><i className="fa-solid fa-arrow-left-long" /> Back</Link>
                                </li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>

            <div className="content">
                <div className="container-fluid">
                    <div className="row">
                        <div className="frmwdth_cntr">
                            <div className="col-lg-12">
                                <div className="card card-primary card-outline">
                                    {loading ? (
                                        <div className="card-body text-center">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="sr-only">Loading...</span>
                                            </div>
                                        </div>
                                    ) : (
                                        <form className="form-horizontal">
                                            <div className="card-body">

                                                {/* ── Basic Info ── */}
                                                <h6 className="text-muted text-uppercase mb-3" style={{ letterSpacing: '0.08em', fontSize: '0.75rem' }}>Basic Info</h6>

                                                <Field label="Name" value={user.name} />
                                                <Field label="Email" value={user.email} />
                                                <Field label="Phone" value={user.phone} />
                                                <Field
                                                    label="Role"
                                                    value={<span className="badge bg-secondary text-white px-2 py-1">{user.role || '-'}</span>}
                                                />
                                                <Field
                                                    label="Source"
                                                    value={<span className="text-capitalize">{user.source || 'circleone'}</span>}
                                                />
                                                <Field
                                                    label="Referral Source"
                                                    value={user.acquisition_utm_source || '-'}
                                                />
                                                <Field
                                                    label="Status"
                                                    value={
                                                        user.status === 'active'
                                                            ? <span className="f_green fwt_600">Active</span>
                                                            : <span className="f_red fwt_600">Inactive</span>
                                                    }
                                                />

                                                <hr />

                                                {/* ── Security ── */}
                                                <h6 className="text-muted text-uppercase mb-3" style={{ letterSpacing: '0.08em', fontSize: '0.75rem' }}>Security</h6>

                                                <div className="form-group row align-items-center">
                                                    <label className="col-12 col-md-4 col-form-label fwt_600">2FA</label>
                                                    <div className="col-12 col-md-8 d-flex align-items-center" style={{ gap: '12px' }}>
                                                        <span className={user.mfaEnabled ? 'f_green fwt_600' : 'f_red fwt_600'}>
                                                            {user.mfaEnabled ? 'Enabled' : 'Disabled'}
                                                        </span>
                                                        {(userRole === 'superadmin' || userRole === 'admin') && (
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-warning"
                                                                onClick={handleReset2FA}
                                                            >
                                                                Reset 2FA
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>

                                                <hr />

                                                {/* ── Subscription / Plan ── */}
                                                <h6 className="text-muted text-uppercase mb-3" style={{ letterSpacing: '0.08em', fontSize: '0.75rem' }}>Subscription</h6>

                                                <Field label="Plan" value={planLabel()} />
                                                <Field
                                                    label="Subscription Status"
                                                    value={<span className="text-capitalize">{user.subscriptionStatus || user.subscription_status || 'none'}</span>}
                                                />
                                                {user.currentPlan?.name && (
                                                    <Field label="Plan Name" value={user.currentPlan.name} />
                                                )}
                                                {user.currentPlan?.interval && (
                                                    <Field label="Billing Interval" value={<span className="text-capitalize">{user.currentPlan.interval}</span>} />
                                                )}
                                                <Field label="Subscription Since" value={formatDate(user.subscription_created_at)} />
                                                {/* Locked/unlocked, how, renewal, auto-unlock (#199 Phase 2).
                                                    CircleOne-only, same gate as the receipts link below. */}
                                                {canViewReceipts && <MemberUnlockStatus memberId={id} />}

                                                <hr />

                                                {/* ── Activity ── */}
                                                <h6 className="text-muted text-uppercase mb-3" style={{ letterSpacing: '0.08em', fontSize: '0.75rem' }}>Activity</h6>

                                                <Field label="Created Date" value={formatDate(user.createdAt)} />
                                                <Field label="Last Login" value={formatDate(user.lastActivity)} />
                                                <Field label="Invited" value={user.invited ? 'Yes' : 'No'} />
                                                <Field label="Invited At" value={formatDate(user.invitedAt)} />

                                                <hr />

                                                {/* ── Loyalty ── */}
                                                {/* Denormalized loyalty* snapshot on the user record (kept fresh by
                                                    membership/loyalty/loyaltyUserStats.js). Same figures the
                                                    Users list shows as columns — Scanned / Receipts / Last Receipt /
                                                    Earned / Balance / Redeemed. "Scanned" and the count are ACCEPTED
                                                    receipts only. */}
                                                <h6 className="text-muted text-uppercase mb-3" style={{ letterSpacing: '0.08em', fontSize: '0.75rem' }}>Loyalty</h6>

                                                <Field
                                                    label="Scanned"
                                                    value={
                                                        user.loyaltyHasScanned
                                                            ? <span className="f_green fwt_600">Yes</span>
                                                            : <span className="text-muted">No</span>
                                                    }
                                                />
                                                <Field label="Receipts" value={user.loyaltyReceiptCount ?? 0} />
                                                <Field label="Last Receipt" value={formatDate(user.loyaltyLastReceiptAt)} />
                                                {canViewReceipts && (
                                                    <Field
                                                        label="Receipt History"
                                                        value={<Link to={`/loyaltyMembers/${id}`}>Receipts and products bought</Link>}
                                                    />
                                                )}
                                                <Field label="Points Earned" value={Number.isFinite(user.loyaltyPointsEarned) ? user.loyaltyPointsEarned : 0} />
                                                <Field label="Balance" value={dollars(user.loyaltyBalanceCents)} />
                                                <Field
                                                    label="Redeemed"
                                                    value={
                                                        user.loyaltyEverRedeemed
                                                            ? <span className="f_green fwt_600">Yes</span>
                                                            : <span className="text-muted">No</span>
                                                    }
                                                />

                                                {/* Points history — live from squad-rewards' ledger via the admin
                                                    proxy (GET /admin/loyalty/members/:id/balance), the same list the
                                                    member sees on the Loyalty Center. Best-effort: a failed/again load
                                                    just shows a short note, the snapshot fields above still stand. */}
                                                <div className="form-group row">
                                                    <label className="col-12 col-md-4 col-form-label fwt_600">Points History</label>
                                                    <div className="col-12 col-md-8">
                                                        {loyalty.status === 'loading' && (
                                                            <span className="text-muted">Loading…</span>
                                                        )}
                                                        {loyalty.status === 'error' && (
                                                            <span className="text-muted">Temporarily unavailable.</span>
                                                        )}
                                                        {loyalty.status === 'ready' && (() => {
                                                            const items = (Array.isArray(loyalty.data.history) ? loyalty.data.history : [])
                                                                .filter(isValidHistoryItem);
                                                            if (items.length === 0) {
                                                                return <span className="text-muted">No points activity yet.</span>;
                                                            }
                                                            const totalPages = Math.ceil(items.length / HISTORY_PAGE_SIZE);
                                                            // Clamp rather than trust historyPage — a stale value can outlive
                                                            // the list it was paging (e.g. React re-uses this component for a
                                                            // different :id).
                                                            const page = Math.min(Math.max(historyPage, 1), totalPages);
                                                            const shown = items.slice((page - 1) * HISTORY_PAGE_SIZE, page * HISTORY_PAGE_SIZE);
                                                            return (
                                                                <>
                                                                    <table className="table table-sm mb-1">
                                                                        <tbody>
                                                                            {shown.map((h) => (
                                                                                <tr key={`${h.type}-${h.id}`}>
                                                                                    <td>{historyItemLabel(h)}</td>
                                                                                    <td className="text-muted text-nowrap">{formatDate(h.date)}</td>
                                                                                    <td className={`text-right text-nowrap fwt_600 ${h.type === 'redemption' ? 'text-warning' : 'f_green'}`}>
                                                                                        {historyItemPoints(h)}
                                                                                    </td>
                                                                                </tr>
                                                                            ))}
                                                                        </tbody>
                                                                    </table>
                                                                    {totalPages > 1 && (
                                                                        <div className="d-flex align-items-center" style={{ gap: '12px' }}>
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-sm btn-outline-secondary"
                                                                                onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
                                                                                disabled={page <= 1}
                                                                            >
                                                                                Previous
                                                                            </button>
                                                                            <small className="text-muted">
                                                                                Page {page} of {totalPages} · {items.length} entries
                                                                            </small>
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-sm btn-outline-secondary"
                                                                                onClick={() => setHistoryPage((p) => Math.min(totalPages, p + 1))}
                                                                                disabled={page >= totalPages}
                                                                            >
                                                                                Next
                                                                            </button>
                                                                        </div>
                                                                    )}
                                                                </>
                                                            );
                                                        })()}
                                                    </div>
                                                </div>

                                                <hr />

                                                {/* ── Acquisition / Funnel ── */}
                                                <h6 className="text-muted text-uppercase mb-3" style={{ letterSpacing: '0.08em', fontSize: '0.75rem' }}>Acquisition</h6>

                                                <Field label="Acquisition Funnel" value={user.acquisition_funnel} />
                                                <Field label="Last Funnel Touch" value={user.last_funnel_touch} />
                                                <Field label="Last Funnel Touch At" value={formatDateTime(user.last_funnel_touch_at)} />
                                                <Field label="UTM Source" value={user.acquisition_utm_source} />
                                                <Field label="UTM Medium" value={user.acquisition_utm_medium} />
                                                <Field label="UTM Campaign" value={user.acquisition_utm_campaign} />
                                                <Field label="UTM Content" value={user.acquisition_utm_content} />
                                                <Field label="UTM Term" value={user.acquisition_utm_term} />
                                                <Field label="Facebook Click ID (fbclid)" value={user.acquisition_fbclid} />
                                                <Field label="Facebook Click ID (fbc)" value={user.acquisition_fbc} />
                                                <Field label="Google Click ID (gclid)" value={user.acquisition_gclid} />
                                                <Field label="Referral Code Used" value={user.acquisition_ref_code} />
                                                <Field
                                                    label="Provisional"
                                                    value={
                                                        user.acquisition_provisional
                                                            ? <span className="text-warning fwt_600">Yes</span>
                                                            : <span className="f_green fwt_600">No</span>
                                                    }
                                                />

                                            </div>

                                            {/* Delete is available from the Users list page to keep
                                                destructive actions one step away from the detail view */}
                                            <div className="card-footer text-center">
                                                <Link to={`/usersedit/${id}`} className="btn btn-blue mr-2">Edit</Link>
                                            </div>
                                        </form>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Users_View;
